<script setup>
import { h, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useIsMobile } from 'dashboard/composables/useMobileLayout';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { setPageTitle } from 'dashboard/helper/pageTitle';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import PhoneControlsCompact from '../../components/PhoneControlsCompact.vue';
import PhoneConnectionErrorDialog from '../../components/PhoneConnectionErrorDialog.vue';
import DropdownBody from 'dashboard/components-next/dropdown-menu/base/DropdownBody.vue';
import DropdownContainer from 'dashboard/components-next/dropdown-menu/base/DropdownContainer.vue';
import DropdownItem from 'dashboard/components-next/dropdown-menu/base/DropdownItem.vue';
import DropdownSeparator from 'dashboard/components-next/dropdown-menu/base/DropdownSeparator.vue';
import { DATA_ASSISTANT_ENABLED } from 'dashboard/constants/dataAssistant';

defineProps({
  isMobileSidebarOpen: { type: Boolean, default: false },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const hasDraft = computed(
  () => store.getters['messageDrafts/getAllDrafts'].length > 0
);

const composeRef = ref(null);

const allDrafts = computed(() => store.getters['messageDrafts/getAllDrafts']);

watch(
  allDrafts,
  drafts => {
    const getContact = store.getters['contacts/getContactById'];
    drafts.forEach(draft => {
      if (!draft.contactId) return;
      const contact = getContact(draft.contactId);
      if (!contact?.id) {
        store.dispatch('contacts/show', { id: draft.contactId });
      }
    });
  },
  { immediate: true }
);

const draftsForDisplay = computed(() => {
  const drafts = allDrafts.value;
  const getContact = store.getters['contacts/getContactById'];
  return drafts.map(draft => {
    const contact = draft.contactId ? getContact(draft.contactId) : null;
    const recipient = contact?.name || draft.toEmails?.[0] || null;
    const subject = draft.subject?.trim() || null;
    const parts = [recipient, subject].filter(Boolean);
    const label = parts.join(' - ') || t('SIDEBAR.DRAFT');
    return { id: draft.id, label, icon: 'i-lucide-file-text' };
  });
});

const openCompose = draftId => {
  composeRef.value?.openWithDraft(draftId);
};

const handleComposeClick = () => {
  if (hasDraft.value) return;
  openCompose(null);
};

const route = useRoute();
const router = useRouter();
const selectedChat = useMapGetter('getSelectedChat');

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

const isMobile = useIsMobile();
// Phones show the sidebar as a full width drawer, never collapsed
const isCollapsed = computed(
  () => !isMobile.value && (uiSettings.value.sidebar_collapsed || false)
);

useEmitter(BUS_EVENTS.OPEN_NEW_CONVERSATION, async (payload = {}) => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
  if (payload.forwardDraftId) {
    composeRef.value?.openWithDraft(
      payload.forwardDraftId,
      payload.sourceConversationId
    );
  } else {
    composeRef.value?.openWithContext(payload);
  }
});

const handleComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

// Deep links into the compose modal from outside the SPA.
//
// The Chrome extension dispatches this DOM event on the dashboard tab that is
// already open, so an active SIP call in that tab is never torn down by a
// navigation. It is wired here rather than in the phone store because
// setupChromeExtensionListeners only runs once the phone module receives an
// EXTENSION_PONG -- composing has to work on accounts with no phone at all.
const handleExtensionCompose = event => {
  const { email } = event.detail || {};
  if (!email) return;
  emitter.emit(BUS_EVENTS.OPEN_NEW_CONVERSATION, { email });
};

onMounted(() => {
  window.addEventListener(
    'jarabot:conversation:compose',
    handleExtensionCompose
  );

  // Fallback path: /app/compose?email=... redirects here with the address in
  // the query. Strip it afterwards so a reload does not reopen the modal.
  const { compose_email: composeEmail } = route.query;
  if (!composeEmail) return;

  emitter.emit(BUS_EVENTS.OPEN_NEW_CONVERSATION, { email: composeEmail });

  const cleanQuery = { ...route.query };
  delete cleanQuery.compose_email;
  router.replace({ query: cleanQuery });
});

onUnmounted(() => {
  window.removeEventListener(
    'jarabot:conversation:compose',
    handleExtensionCompose
  );
});

const phoneControlsVisible = useMapGetter('phone/getPhoneControlsVisible');
const toggleSidebar = () => {
  if (isMobile.value) {
    emit('closeMobileSidebar');
    return;
  }
  updateUISettings({ sidebar_collapsed: !isCollapsed.value });
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

// inboxes marked for deletion stay out of the sidebar; their conversations are still in the lists
const inboxes = useMapGetter('inboxes/getActiveInboxesWithInternal');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const stats = useMapGetter('sidebarStats/getStats');
const currentRole = useMapGetter('getCurrentRole');
const currentAccount = useMapGetter('getCurrentAccount');
const isAdminRole = computed(() => currentRole.value === 'administrator');
const isDataAssistantManager = computed(
  () =>
    isAdminRole.value ||
    (currentAccount.value?.permissions || []).includes('data_assistant_manage')
);
const dataAssistantReports = useMapGetter('dataAssistantReports/getReports');
const hasDataAssistantReports = computed(
  () => (dataAssistantReports.value || []).length > 0
);
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const nestedConversationCustomViews = useMapGetter(
  'customViews/getNestedConversationCustomViews'
);

const fetchStats = () =>
  store.dispatch('sidebarStats/get', {
    inboxes: inboxes.value,
    views: conversationCustomViews.value,
  });

const { startRefetching } = useLiveRefresh(
  fetchStats,
  import.meta.env.DEV ? 120000 : 10000
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('integrations/get');
  store.dispatch('messageDrafts/fetchAll');
  store.dispatch('dataAssistantReports/fetchReports');
  startRefetching();
});

// A cache revalidation replaces the inbox array even when nothing changed.
// Refresh the counts only when the set of inboxes changes.
const inboxIdsKey = computed(() =>
  inboxes.value
    .map(inbox => inbox.id)
    .sort((a, b) => a - b)
    .join(',')
);

watch(inboxIdsKey, () => {
  store.dispatch('sidebarStats/get', {
    inboxes: inboxes.value,
  });
});

watch(conversationCustomViews, () => {
  store.dispatch('sidebarStats/get', {
    views: conversationCustomViews.value,
  });
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
    activeOn: ['label_reports_show'],
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
  {
    name: 'Reports Phone',
    label: t('SIDEBAR.REPORTS_PHONE'),
    to: accountScopedRoute('phone_reports_index'),
    activeOn: ['phone_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const sidebarConfig = computed(() => ({
  show_all_conversations: true,
  show_inbox: true,
  show_conversation_groups: true,
  show_folders: true,
  show_teams: true,
  show_channels: true,
  show_labels: true,
  ...(uiSettings.value.sidebar_config || {}),
}));

const menuItems = computed(() => {
  const items = [];

  if (sidebarConfig.value.show_inbox) {
    items.push({
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        badge: 'notifications/getHasUnreadNotifications',
      },
    });
  }

  const conversationChildren = [];
  const allConversationsItem = {
    name: 'All',
    label: t('SIDEBAR.ALL_CONVERSATIONS'),
    activeOn: ['inbox_conversation'],
    to: accountScopedRoute('home'),
  };
  if (sidebarConfig.value.show_all_conversations) {
    conversationChildren.push(allConversationsItem);
  }
  if (sidebarConfig.value.show_conversation_groups) {
    conversationChildren.push(
      {
        name: 'Mentions',
        label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
        activeOn: ['conversation_through_mentions'],
        to: accountScopedRoute('conversation_mentions'),
      },
      {
        name: 'Unattended',
        activeOn: ['conversation_through_unattended'],
        label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
        to: accountScopedRoute('conversation_unattended'),
      }
    );
  }
  if (sidebarConfig.value.show_folders) {
    const mapCustomViewsToMenuItems = views => {
      return views.map(view => {
        const menuItem = {
          name: `${view.name}-${view.id}`,
          label: view.name,
          count: stats.value.views[view.id]?.all_count,
          to: accountScopedRoute('folder_conversations', { id: view.id }),
          folderId: view.id,
          color: view.color,
        };

        if (view.children && view.children.length > 0) {
          menuItem.children = view.children.map(child => ({
            name: `${child.name}-${child.id}`,
            label: child.name,
            count: stats.value.views[child.id]?.all_count,
            to: accountScopedRoute('folder_conversations', { id: child.id }),
            folderId: child.id,
            color: child.color,
          }));
        }

        return menuItem;
      });
    };

    conversationChildren.push({
      name: 'Folders',
      label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
      icon: 'i-lucide-folder',
      activeOn: ['conversations_through_folders'],
      children: mapCustomViewsToMenuItems(nestedConversationCustomViews.value),
    });
  }
  if (sidebarConfig.value.show_teams) {
    conversationChildren.push({
      name: 'Teams',
      label: t('SIDEBAR.TEAMS'),
      icon: 'i-lucide-users',
      activeOn: ['conversations_through_team'],
      children: teams.value.map(team => ({
        name: `${team.name}-${team.id}`,
        label: team.name,
        to: accountScopedRoute('team_conversations', { teamId: team.id }),
      })),
    });
  }
  if (sidebarConfig.value.show_channels) {
    conversationChildren.push({
      name: 'Channels',
      label: t('SIDEBAR.CHANNELS'),
      icon: 'i-lucide-mailbox',
      activeOn: ['conversation_through_inbox'],
      children: sortedInboxes.value.map(inbox => ({
        name: `${inbox.name}-${inbox.id}`,
        label: inbox.name,
        to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
        component: leafProps =>
          h(ChannelLeaf, {
            label: leafProps.label,
            active: leafProps.active,
            count: stats.value.inboxes[inbox.id]?.all_count,
            inbox,
          }),
      })),
    });
  }
  if (sidebarConfig.value.show_labels) {
    conversationChildren.push({
      name: 'Labels',
      label: t('SIDEBAR.LABELS'),
      icon: 'i-lucide-tag',
      activeOn: ['conversations_through_label'],
      children: labels.value.map(label => ({
        name: `${label.title}-${label.id}`,
        label: label.title,
        icon: h('span', {
          class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
          style: { backgroundColor: label.color },
        }),
        to: accountScopedRoute('label_conversations', {
          label: label.title,
        }),
      })),
    });
  }

  if (conversationChildren.length === 0) {
    conversationChildren.push(allConversationsItem);
  }
  // For conversations menu: hide subgroup headers if there's only one subgroup and no direct items
  const directItems = conversationChildren.filter(child => child.to);
  const subGroups = conversationChildren.filter(child => child.children);
  if (directItems.length === 0 && subGroups.length === 1) {
    subGroups[0].hideHeader = true;
  }
  items.push({
    name: 'Conversation',
    label: t('SIDEBAR.CONVERSATIONS'),
    icon: 'i-lucide-message-circle',
    children: conversationChildren,
  });

  items.push(
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      children: [
        {
          name: 'Assistants',
          label: t('SIDEBAR.CAPTAIN_ASSISTANTS'),
          to: accountScopedRoute('captain_assistants_index'),
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          to: accountScopedRoute('captain_documents_index'),
        },
        {
          name: 'Responses',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          to: accountScopedRoute('captain_responses_index'),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
            'portals_articles_versions',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Dictionary',
          label: t('SIDEBAR.HELP_CENTER.DICTIONARY'),
          activeOn: ['portals_dictionary_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_dictionary_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Wallboard',
      label: t('SIDEBAR.WALLBOARD'),
      icon: 'i-lucide-phone-call',
      children: [
        {
          name: 'Overview',
          label: t('SIDEBAR.WALLBOARD_OVERVIEW'),
          to: accountScopedRoute('wallboard'),
          activeOn: ['wallboard'],
        },
        {
          name: 'Queues',
          label: t('SIDEBAR.WALLBOARD_QUEUES'),
          to: accountScopedRoute('wallboard_queues'),
          activeOn: ['wallboard_queues'],
        },
      ],
    },
    ...(DATA_ASSISTANT_ENABLED &&
    (isDataAssistantManager.value || hasDataAssistantReports.value)
      ? [
          {
            name: 'DataAssistant',
            label: t('SIDEBAR.DATA_ASSISTANT'),
            icon: 'i-lucide-bot-message-square',
            children: [
              ...(isDataAssistantManager.value
                ? [
                    {
                      name: 'DataAssistant Chat',
                      label: t('SIDEBAR.DATA_ASSISTANT_CHAT'),
                      to: accountScopedRoute('data_assistant_index'),
                      activeOn: ['data_assistant_conversation'],
                    },
                  ]
                : []),
              {
                name: 'DataAssistant Reports',
                label: t('SIDEBAR.DATA_ASSISTANT_REPORTS'),
                to: accountScopedRoute('data_assistant_reports_index'),
                activeOn: ['data_assistant_report_show'],
              },
            ],
          },
        ]
      : []),
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Blocked Domains',
          label: t('SIDEBAR.BLOCKED_DOMAINS'),
          icon: 'i-lucide-ban',
          to: accountScopedRoute('blocked_domains_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Templates',
          label: t('SIDEBAR.WHATSAPP_TEMPLATES'),
          icon: 'i-lucide-layout-template',
          to: accountScopedRoute('settings_templates'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    }
  );

  return items;
});

// Returns all navigable leaves (items with `to` or `activeOn`) from a menu tree.
const flatLeaves = item => {
  const self = item.to || item.activeOn ? [item] : [];
  if (!item.children) return self;
  return item.children.reduce(
    (acc, child) => acc.concat(flatLeaves(child)),
    self
  );
};

const isRouteActive = item => {
  try {
    if (item.to && router.resolve(item.to).path === route.path) return true;
  } catch {
    /* empty */
  }
  return item.activeOn?.includes(route.name) ?? false;
};

// The label of the top-level sidebar section that is currently active.
const activeSectionLabel = computed(() => {
  const active = menuItems.value.find(
    item =>
      isRouteActive(item) || flatLeaves(item).some(leaf => isRouteActive(leaf))
  );
  return active?.label || '';
});

watch(
  [activeSectionLabel, selectedChat],
  () => {
    const parts = [];
    const chat = selectedChat.value;
    if (chat) {
      const sender = chat.meta?.sender;
      const identifier =
        sender?.name ||
        sender?.email ||
        sender?.phone_number ||
        chat.additional_attributes?.mail_subject ||
        '';
      if (identifier) parts.push(identifier);
    }
    if (activeSectionLabel.value) parts.push(activeSectionLabel.value);
    setPageTitle(parts.join(' - '));
  },
  { immediate: true }
);
</script>

<template>
  <aside
    class="bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak h-full flex flex-col text-sm pb-1 transition-all duration-200 fixed top-0 ltr:left-0 rtl:right-0 z-40 md:static md:z-auto md:ltr:transform-none md:rtl:transform-none flex-shrink-0"
    :class="[
      isCollapsed ? 'w-12' : 'w-[13.5rem]',
      isMobileSidebarOpen
        ? 'translate-x-0 shadow-lg md:shadow-none'
        : 'ltr:-translate-x-full rtl:translate-x-full',
    ]"
  >
    <section class="grid gap-2 mt-2 mb-4">
      <div class="flex items-center min-w-0 gap-2 px-2">
        <div class="grid flex-shrink-0 size-6 place-content-center">
          <Logo class="size-4" />
        </div>
        <div v-if="!isCollapsed" class="flex-shrink-0 w-px h-3 bg-n-strong" />
        <SidebarAccountSwitcher
          v-if="!isCollapsed"
          class="flex-grow min-w-0 -mx-1"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <div class="flex gap-2 px-2">
        <RouterLink
          v-if="!isCollapsed"
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-2 py-1 rounded-lg h-7 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
          <span class="flex-grow text-left">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <Button
          :icon="
            isCollapsed ? 'i-lucide-chevron-right' : 'i-lucide-chevron-left'
          "
          color="slate"
          size="sm"
          class="!h-7 !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11"
          @click="toggleSidebar"
        />
        <DropdownContainer v-if="!isCollapsed" class="relative">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              size="sm"
              class="!h-7 !outline-n-weak"
              :class="[
                hasDraft
                  ? '!bg-primary-600 dark:!bg-primary-500 !text-white hover:!bg-primary-700 dark:hover:!bg-primary-600'
                  : '!bg-n-solid-3 dark:!bg-n-black/30 !text-n-slate-11',
              ]"
              @click="hasDraft ? toggle() : handleComposeClick()"
            />
          </template>
          <DropdownBody
            strong
            class="top-full mt-1 ltr:left-0 w-72 z-50 [&>ul]:!bg-n-solid-3 [&>ul]:!backdrop-blur-none"
          >
            <DropdownItem
              :label="t('SIDEBAR.NEW_CONVERSATION')"
              icon="i-lucide-plus"
              :click="() => openCompose(null)"
            />
            <DropdownSeparator />
            <DropdownItem
              v-for="draft in draftsForDisplay"
              :key="draft.id"
              :label="draft.label"
              :icon="draft.icon"
              :click="() => openCompose(draft.id)"
            />
          </DropdownBody>
        </DropdownContainer>
      </div>
      <!-- Never guarded by isCollapsed: the modal is a fixed overlay that
           renders nothing until opened, and a null composeRef silently
           swallowed every programmatic open request. -->
      <ComposeConversation ref="composeRef" @close="handleComposeClose" />
    </section>
    <nav class="grid flex-grow gap-2 px-1 pb-5 overflow-y-scroll no-scrollbar">
      <ul v-if="!isCollapsed" class="flex flex-col gap-2 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
      <ul v-else class="flex flex-col gap-1 m-0 list-none">
        <li v-for="item in menuItems" :key="item.name" class="relative">
          <RouterLink
            v-if="item.to"
            :to="item.to"
            class="flex items-center justify-center w-8 h-8 mx-auto rounded-lg hover:bg-n-solid-3 text-n-slate-11 hover:text-n-slate-12 transition-colors"
            :title="item.label"
          >
            <span :class="item.icon" class="size-4" />
          </RouterLink>
          <RouterLink
            v-else-if="
              item.children && item.children.length > 0 && item.children[0].to
            "
            :to="item.children[0].to"
            class="flex items-center justify-center w-8 h-8 mx-auto rounded-lg hover:bg-n-solid-3 text-n-slate-11 hover:text-n-slate-12 transition-colors"
            :title="item.label"
          >
            <span :class="item.icon" class="size-4" />
          </RouterLink>
          <div
            v-else
            class="flex items-center justify-center w-8 h-8 mx-auto rounded-lg hover:bg-n-solid-3 text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer"
            :title="item.label"
          >
            <span :class="item.icon" class="size-4" />
          </div>
        </li>
      </ul>
    </nav>
    <section v-if="!phoneControlsVisible">
      <PhoneControlsCompact :collapsed="isCollapsed" />
    </section>
    <!-- Teleported to body; mounted unconditionally so a failed auto-connect is
         surfaced whatever the sidebar is currently showing. -->
    <PhoneConnectionErrorDialog />
    <section
      class="p-1 border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)] flex-shrink-0 flex justify-between gap-2 items-center"
    >
      <SidebarProfileMenu
        v-if="!isCollapsed"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
      <div v-else class="flex items-center justify-center w-8 h-8 mx-auto">
        <SidebarProfileMenu
          collapsed-mode
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
  </aside>
</template>
